<script setup lang="ts">
import ButtonBase from '@/Common/Elements/Buttons/ButtonBase.vue'
import { ArrowUpIcon, StopIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import type { ChatStatus } from 'ai'
import type { HTMLAttributes } from 'vue'
import { computed } from 'vue'
import { cn } from '../utils'

interface Props {
  class?: HTMLAttributes['class']
  status?: ChatStatus
  disabled?: boolean
  color?: string
  size?: string
  shape?: string
}

const props = withDefaults(defineProps<Props>(), {
  color: 'default',
  size: 'sm',
  shape: 'circular',
})

const emit = defineEmits<{
  (e: 'stop'): void
}>()

const isStopStatus = computed(
  () => props.status === 'submitted' || props.status === 'streaming'
)

const icon = computed(() => {
  if (isStopStatus.value) return StopIcon
  if (props.status === 'error') return XMarkIcon

  return ArrowUpIcon
})

const buttonType = computed(() => (isStopStatus.value ? 'button' : 'submit'))
const ariaLabel = computed(() => (isStopStatus.value ? 'Stop' : 'Submit'))

function handleClick(event: MouseEvent) {
  if (!isStopStatus.value) return

  event.preventDefault()
  emit('stop')
}
</script>

<template>
  <ButtonBase
    :aria-label="ariaLabel"
    :class="cn(props.class)"
    :color="props.color"
    :disabled="props.disabled"
    :shape="props.shape"
    :size="props.size"
    :type="buttonType"
    :leadingIcon="icon"
    @click="handleClick"
  />
</template>
